import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Link, useNavigate, useSearchParams } from "react-router-dom";

import {
  Edit as EditIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { Button, Chip, IconButton, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGrid } from "@mui/x-data-grid";
import { PageContainer } from "@/features/ui";

import { useAuth } from "@/features/authentication";
import {
  AllOutletsSelect,
  OutletRoleEnum,
  UserOutletsSelect,
} from "@/features/outlets";
import { PermissionEnum } from "@/features/permissions";
import { SupplierSelect } from "@/features/suppliers";
import {
  PurchaseOrderStatusAutocomplete,
  statusColors,
  StatusEnum,
  statusLabels,
} from "@/features/purchase-orders";
import { apiOutletClient, formatDate, getErrorMessage } from "@/features/shared";
import {
  CustomDateRangePicker,
  SearchTextField,
  useSnackbar,
} from "@/features/ui";
import {
  useDateRangeFilter,
  useSearchFilter,
  useServerPagination,
} from "@/hooks";

export const PurchaseOrdersLab = () => {
  const { outlet, changeOutlet, hasPermission } = useAuth();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  const {
    setRowCount,
    paginationParams,
    dataGridPaginationProps,
    resetParams,
  } = useServerPagination();
  const { keyword, searchProps } = useSearchFilter({ resetParams });
  const { dateRangeProps, dateParams } = useDateRangeFilter({ resetParams });

  const statusValue = useMemo(() => searchParams.get("status"), [searchParams]);
  const prevOutletRoleRef = useRef();

  // Parse seller from URL params
  const seller = useMemo(() => {
    const sellerParam = searchParams.get("seller");
    if (!sellerParam) return null;
    try {
      return JSON.parse(sellerParam);
    } catch {
      return null;
    }
  }, [searchParams]);

  const loadPurchaseOrders = useCallback(async () => {
    if (!outlet) return;

    setLoading(true);
    try {
      const {
        data: { data, pageInfo },
      } = await apiOutletClient.get("purchase-orders/lab", {
        params: {
          ...paginationParams,
          ...dateParams,
          ...(keyword && { referenceNumber: keyword }),
          status: statusValue || null,
          sellerableId: seller?.id || null,
          withSeller: true,
          withCreator: true,
        },
      });
      setPurchaseOrders(data);
      setRowCount(pageInfo.totalItems);
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memuat pesanan pembelian lab"));
    } finally {
      setLoading(false);
    }
  }, [
    outlet,
    paginationParams,
    dateParams,
    keyword,
    statusValue,
    seller,
    setRowCount,
    showSnackbar,
  ]);

  useEffect(() => {
    loadPurchaseOrders();
  }, [loadPurchaseOrders]);

  // Reset seller when outlet role changes
  useEffect(() => {
    if (
      prevOutletRoleRef.current !== undefined &&
      prevOutletRoleRef.current !== outlet?.role
    ) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete("seller");
        return newParams;
      });
    }
    prevOutletRoleRef.current = outlet?.role;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outlet?.role]);

  const handleSellerChange = (value) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (value) {
        newParams.set(
          "seller",
          JSON.stringify({ id: value.id, name: value.name })
        );
      } else {
        newParams.delete("seller");
      }
      resetParams.forEach((param) => newParams.delete(param));
      return newParams;
    });
  };

  const toolbarContent = hasPermission(
    PermissionEnum.CREATE_PURCHASE_ORDERS_LAB
  ) && (
    <Button variant="contained" component={Link} to="create">
      Tambah
    </Button>
  );

  const columns = [
    {
      field: "referenceNumber",
      headerName: "Nomor #",
      flex: 1,
      sortable: false,
    },
    {
      field: "transactionDate",
      headerName: "Tanggal",
      flex: 1,
      sortable: false,
      valueGetter: (transactionDate) => formatDate(transactionDate),
    },
    {
      field: "creator",
      headerName: "Dibuat Oleh",
      flex: 1,
      sortable: false,
      valueGetter: (creator) => creator?.name ?? "-",
    },
    {
      field: "seller",
      headerName: "Pemasok",
      flex: 1.5,
      sortable: false,
      valueGetter: (seller) => seller?.name ?? "-",
    },
    ...(outlet?.role === OutletRoleEnum.HUB
      ? [
          {
            field: "internalBuyer",
            headerName: "Pembeli Internal",
            flex: 1,
            sortable: false,
            renderCell: ({ row: { internalPurchaseOrder } }) =>
              internalPurchaseOrder.buyer?.name ?? "-",
          },
        ]
      : []),
    {
      field: "customer",
      headerName: "Pelanggan",
      flex: 1,
      sortable: false,
      renderCell: ({ row: { internalPurchaseOrder } }) =>
        internalPurchaseOrder.customer?.name ?? "-",
    },
    {
      field: "lensProduct",
      headerName: "Lensa",
      flex: 1.5,
      sortable: false,
      renderCell: ({ row }) => {
        if (!row.purchaseOrderProductVariants?.length) return "-";

        return row.purchaseOrderProductVariants[0].product.name;
      },
    },
    {
      field: "variant",
      headerName: "Varian",
      flex: 2,
      sortable: false,
      renderCell: ({ row }) => {
        if (!row.purchaseOrderProductVariants?.length) return "-";

        return (
          <Stack spacing={0.5}>
            {row.purchaseOrderProductVariants.map((variant) => {
              const sideLabel = variant.side === "LEFT" ? "L" : "R";

              // Get axis from note
              let axis = "-";
              try {
                const parsed = JSON.parse(variant.note || "{}");
                axis = parsed.axis || "0";
              } catch {
                // Ignore JSON parse errors
              }

              // Parse prescription parts: {sph}/{cyl}/{add?}/{side?}
              const prescriptionParts = variant.name
                ?.substring(variant.name.lastIndexOf(" ") + 1)
                .split("/");
              const sph = prescriptionParts[0] || "-";
              const cyl = prescriptionParts[1] || "-";
              const add = prescriptionParts[2] || "0"; // Use "0" if null
              // Ignore prescriptionParts[3] (side indicator)

              // Build prescription
              const prescription = `${sph}/${cyl}/${axis}/${add}`;

              return (
                <span key={variant.id}>
                  {sideLabel}: {prescription}
                </span>
              );
            })}
          </Stack>
        );
      },
    },

    {
      field: "status",
      headerName: "Status",
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => (
        <Chip
          label={statusLabels[row.status]}
          color={statusColors[row.status]}
        />
      ),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Aksi",
      getActions: ({ row: { referenceNumber, status } }) => {
        const actions = [];
        if (hasPermission(PermissionEnum.READ_PURCHASE_ORDERS_LAB)) {
          actions.push(
            <IconButton
              key="view"
              onClick={() => navigate(referenceNumber)}
              size="small"
            >
              <VisibilityIcon />
            </IconButton>
          );
        }
        if (
          hasPermission(PermissionEnum.UPDATE_PURCHASE_ORDERS_LAB) &&
          status == StatusEnum.PENDING
        ) {
          actions.push(
            <IconButton
              key="edit"
              onClick={() => navigate(`${referenceNumber}/edit`)}
              size="small"
            >
              <EditIcon />
            </IconButton>
          );
        }
        return actions;
      },
    },
  ];

  return (
    <PageContainer
      title="Pesanan Pembelian Lab."
      breadcrumbs={[]}
      slotProps={{
        header: { slotProps: { toolbar: { children: toolbarContent } } },
      }}
      maxWidth={false}
    >
      <Stack spacing={3}>
        <Grid container spacing={2} sx={{ justifyContent: "flex-end", alignItems: "end" }}>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <PurchaseOrderStatusAutocomplete
              value={
                statusValue
                  ? {
                      value: statusValue,
                      label: statusLabels[statusValue],
                    }
                  : null
              }
              onChange={(_, selectedOption) => {
                setSearchParams((prev) => {
                  const newParams = new URLSearchParams(prev);
                  if (selectedOption?.value) {
                    newParams.set("status", selectedOption.value);
                  } else {
                    newParams.delete("status");
                  }
                  resetParams.forEach((param) => newParams.delete(param));
                  return newParams;
                });
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            {outlet?.role === OutletRoleEnum.HUB ? (
              <SupplierSelect
                value={seller}
                onChange={handleSellerChange}
                TextFieldProps={{
                  label: "Pemasok (Supplier)",
                }}
              />
            ) : (
              <AllOutletsSelect
                value={seller}
                onChange={handleSellerChange}
                allowedRoles={[OutletRoleEnum.HUB]}
                TextFieldProps={{
                  label: "Pemasok (Hub)",
                }}
              />
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <UserOutletsSelect
              value={outlet}
              onChange={changeOutlet}
              TextFieldProps={{
                label: "Pembeli",
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <SearchTextField
              fullWidth
              placeholder="Cari nomor referensi"
              {...searchProps}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 12, lg: 6 }}>
            <CustomDateRangePicker {...dateRangeProps} />
          </Grid>
        </Grid>
        <DataGrid
          rows={purchaseOrders}
          loading={loading}
          getRowHeight={() => "auto"}
          columns={columns}
          {...dataGridPaginationProps}
          disableRowSelectionOnClick
        />
      </Stack>
    </PageContainer>
  );
};
